import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import classNames from 'classnames';
import { FiX } from 'react-icons/fi';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import SearchInput from '../../components/SearchInput';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { fetchDeleteUser, fetchUpdateUser, fetchUsers } from '../../app/users/users.slice';
import styles from './Users.module.sass';

dayjs.extend(relativeTime)

const LEVEL_COLORS = {
  Newbie: 'default',
  Beginner: 'info',
  Begginer: 'info', // historical typo in DB
  Intermediate: 'warning',
  Advanced: 'success',
  Master: 'error',
};

const LEVEL_OPTIONS = ['Newbie', 'Beginner', 'Intermediate', 'Advanced', 'Master'];

/** Normalize level for display (DB has legacy "Begginer" typo) */
const displayLevel = (level) => (level === 'Begginer' ? 'Beginner' : level);

/** Map UI level to API param (DB stores "Begginer" not "Beginner") */
const apiLevelForFilter = (level) => (level === 'Beginner' ? 'Begginer' : level);

const ROLE_OPTIONS = [
  { value: 'ADMIN', labelKey: 'usersFilter.admin' },
  { value: 'USER', labelKey: 'usersFilter.user' },
  { value: 'TEACHER', labelKey: 'usersFilter.teacher' },
];

const UsersComponent = () => {
  const { t } = useTranslation();
  const { users } = useSelector((state) => state);
  const dispatch = useDispatch();

  const location = useLocation();
  const urlSearch = new URLSearchParams(location.search).get('search') || '';

  const [isEditModal, setIsEditModal] = useState(false)
  const [isDeleteModal, setIsDeleteModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState(urlSearch)
  const [filterLevel, setFilterLevel] = useState(null)
  const [filterRole, setFilterRole] = useState(null)

  const pagination = users?.pagination;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('lastActivity');

  const [userName, setUserName] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [userRole, setUserRole] = useState('')

  const isLoadingUsers = users?.isLoading ?? false;
  const loading = isLoadingUsers || isLoading;

  const getFetchParams = (overrides = {}) => {
    const level = overrides.level ?? filterLevel;
    const params = {
      skip: overrides.skip ?? page,
      take: overrides.take ?? rowsPerPage,
      search: overrides.search ?? search,
      order: overrides.order ?? order,
      orderBy: overrides.orderBy ?? orderBy,
      role: overrides.role ?? filterRole ?? undefined,
      ...overrides,
    };
    params.level = level ? apiLevelForFilter(level) : undefined;
    return params;
  };

  useEffect(() => {
    if (selectedUser) {
      setUserName(selectedUser?.name || '')
      setUserEmail(selectedUser?.email || '')
      setUserRole(selectedUser?.role || '')
    }
  }, [selectedUser])

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      await dispatch(fetchUsers(getFetchParams()));
      if (!cancelled) setIsLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- initial load only; handlers refetch on page/search/order change

  // Deep-links tipo /users?search=… (p. ej. desde la búsqueda global del Home)
  // mientras la pantalla ya está montada.
  useEffect(() => {
    if (urlSearch && urlSearch !== search) {
      setSearch(urlSearch);
      setPage(0);
      dispatch(fetchUsers(getFetchParams({ skip: 0, search: urlSearch })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearch])

  const handleDeleteUser = async () => {
    let result = await dispatch(fetchDeleteUser(selectedUser))
    if (result?.payload?._id) {
      toast.success(t('toast.userDeleted', { name: selectedUser?.name }))
      setIsDeleteModal(false)
    }
  }

  const handleUpdateUser = async () => {
    await dispatch(fetchUpdateUser({
      _id: selectedUser?._id,
      name: userName,
      email: userEmail,
      role: userRole,
    }))
    await dispatch(fetchUsers(getFetchParams()))
    toast.success(t('toast.userUpdated', { name: selectedUser?.name }))
    setIsEditModal(false)
  }

  const handleSearchUser = (value) => {
    setSearch(value)
    setPage(0)
    dispatch(fetchUsers(getFetchParams({ skip: 0, search: value })))
  }

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    dispatch(fetchUsers(getFetchParams({ order: newOrder, orderBy: property })));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    dispatch(fetchUsers(getFetchParams({ skip: newPage })));
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    dispatch(fetchUsers(getFetchParams({ skip: 0, take: newRowsPerPage })));
  };

  const handleFilterLevel = (level) => {
    const newLevel = filterLevel === level ? null : level
    setFilterLevel(newLevel)
    setPage(0)
    dispatch(fetchUsers(getFetchParams({ skip: 0, level: newLevel || undefined })))
  }

  const handleFilterRole = (role) => {
    const newRole = filterRole === role ? null : role
    setFilterRole(newRole)
    setPage(0)
    dispatch(fetchUsers(getFetchParams({ skip: 0, role: newRole || undefined })))
  }

  const handleClearFilters = () => {
    setFilterLevel(null)
    setFilterRole(null)
    setPage(0)
    dispatch(fetchUsers(getFetchParams({ skip: 0, level: undefined, role: undefined })))
  }

  const hasActiveFilters = filterLevel != null || filterRole != null

  const userList = users?.value || []

  const formatTime = (totalTime) => {
    const num = Number(totalTime)
    if (!num || Number.isNaN(num) || num < 0) return '0'
    const hours = Math.floor(num / 3600)
    const minutes = Math.floor((num % 3600) / 60)
    const seconds = Math.floor(num % 60)
    const parts = []
    if (hours > 0) parts.push(`${String(hours).padStart(2, '0')}h`)
    if (minutes > 0) parts.push(`${String(minutes).padStart(2, '0')}m`)
    parts.push(`${String(seconds).padStart(2, '0')}s`)
    return parts.join(' ')
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Paper sx={{ borderRadius: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <Box sx={{ p: 3, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h6" fontWeight={700}>{t('pages.users')}</Typography>
            <Chip label={loading ? '...' : (pagination?.total ?? userList.length)} size="small" sx={{ fontWeight: 700, fontSize: 13 }} />
          </Stack>
          <SearchInput
            placeholder={t('users.searchUser')}
            value={search}
            onChange={handleSearchUser}
            onClear={() => handleSearchUser('')}
            size="medium"
            className={styles.searchInput}
          />
        </Box>

        {/* Filters inline above table */}
        <Box sx={{ px: 3, pb: 2, flexShrink: 0 }}>
          <div className={styles.filterAdvancedRow}>
            <div className={styles.filterGroup}>
              <span className={styles.filterGroupLabel}>{t('catalogFilter.level')}</span>
              <button
                type="button"
                className={classNames(styles.levelChip, { [styles.active]: !filterLevel })}
                onClick={() => handleFilterLevel(null)}
              >
                {t('common.all')}
              </button>
              {LEVEL_OPTIONS.map((l) => (
                <button
                  key={l}
                  type="button"
                  className={classNames(styles.levelChip, styles[`level${l}`], { [styles.active]: filterLevel === l })}
                  onClick={() => handleFilterLevel(l)}
                >
                  {l}
                </button>
              ))}
            </div>
            <div className={styles.filterGroupSep} />
            <div className={styles.filterGroup}>
              <span className={styles.filterGroupLabel}>{t('usersFilter.role')}</span>
              <button
                type="button"
                className={classNames(styles.badgeChip, styles.roleChip_all, { [styles.active]: !filterRole })}
                onClick={() => handleFilterRole(null)}
              >
                {t('common.all')}
              </button>
              {ROLE_OPTIONS.map(({ value, labelKey }) => (
                <button
                  key={value}
                  type="button"
                  className={classNames(styles.badgeChip, styles[`roleChip_${value}`], { [styles.active]: filterRole === value })}
                  onClick={() => handleFilterRole(value)}
                >
                  {t(labelKey)}
                </button>
              ))}
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                className={styles.clearAllBtn}
                onClick={handleClearFilters}
              >
                <FiX size={12} />
                {t('common.clearAll')}
                <span className={styles.clearAllCount}>{[filterLevel, filterRole].filter(Boolean).length}</span>
              </button>
            )}
          </div>
        </Box>

        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    {t('users.user')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <TableSortLabel
                    active={orderBy === 'level'}
                    direction={orderBy === 'level' ? order : 'asc'}
                    onClick={() => handleRequestSort('level')}
                  >
                    {t('common.level')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <TableSortLabel
                    active={orderBy === 'timeSpent'}
                    direction={orderBy === 'timeSpent' ? order : 'asc'}
                    onClick={() => handleRequestSort('timeSpent')}
                  >
                    {t('users.timeSpent')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <TableSortLabel
                    active={orderBy === 'knownCards'}
                    direction={orderBy === 'knownCards' ? order : 'asc'}
                    onClick={() => handleRequestSort('knownCards')}
                  >
                    {t('users.knownCards')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <TableSortLabel
                    active={orderBy === 'streak'}
                    direction={orderBy === 'streak' ? order : 'asc'}
                    onClick={() => handleRequestSort('streak')}
                  >
                    {t('users.streak')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <TableSortLabel
                    active={orderBy === 'numPlayed'}
                    direction={orderBy === 'numPlayed' ? order : 'asc'}
                    onClick={() => handleRequestSort('numPlayed')}
                  >
                    {t('users.sessions')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <TableSortLabel
                    active={orderBy === 'lastActivity'}
                    direction={orderBy === 'lastActivity' ? order : 'asc'}
                    onClick={() => handleRequestSort('lastActivity')}
                  >
                    {t('users.lastActivity')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('users.streakLargest')}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }} align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && userList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">{t('common.loading') || 'Loading...'}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                userList.map((user, index) => (
                  <TableRow
                    key={user?._id || index}
                    hover
                    sx={{ '&:last-child td': { border: 0 } }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>{user?.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={displayLevel(user?.onboard?.level) || '-'}
                        size="small"
                        color={LEVEL_COLORS[user?.onboard?.level] || 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user?.stats?.totalTime ? formatTime(user.stats.totalTime) : '0'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user?.stats?.cardsKnowledge || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user?.stats?.streak ?? 0}
                        size="small"
                        color={user?.stats?.streak > 0 ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user?.stats?.numPlayed ?? 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user?.lastUpdate ? dayjs(user.lastUpdate).from(new Date()) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user?.stats?.streakLargest ?? 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setSelectedUser(user)
                            setIsEditModal(true)
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedUser(user)
                            setIsDeleteModal(true)
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={pagination?.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ '& .MuiTablePagination-selectLabel': { display: 'none' } }}
        />
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={isEditModal} onClose={() => setIsEditModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('users.editUser')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label={t('users.name')}
              fullWidth
              required
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
            />
            <TextField
              label={t('users.email')}
              fullWidth
              required
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel>{t('users.role')}</InputLabel>
              <Select
                value={userRole}
                label={t('users.role')}
                onChange={(e) => setUserRole(e.target.value)}
              >
                <MenuItem value="ADMIN">ADMIN</MenuItem>
                <MenuItem value="USER">USER</MenuItem>
                <MenuItem value="TEACHER">TEACHER</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setIsEditModal(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleUpdateUser}>{t('common.update')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteModal} onClose={() => setIsDeleteModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('users.deleteUser')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete {selectedUser?.name}?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setIsDeleteModal(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDeleteUser}>{t('users.deleteUser')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UsersComponent;
